// src/composables/ui/useDialog.js
import { ref, readonly, computed, createApp, getCurrentInstance } from "vue";
import { logger } from "@misa/ui-base";
import i18n from "@/i18ns/i18n";

// Đăng ký các popup components với lazy loading
const POPUP_COMPONENTS = {};

// Singleton state cho popup manager
const activePopups = ref([]);
const popupCounter = ref(0);
const componentCache = new Map(); // Cache component class để tối ưu performance

// Cache appContext từ host app — được capture khi useDialog() lần đầu được gọi trong component
let _hostAppContext = null;

export function useDialog() {
  // Capture appContext từ component đang gọi useDialog (nếu chưa có)
  // Dùng để kế thừa components/directives đã đăng ký (như MisaUI) thay vì app.use() lại
  const instance = getCurrentInstance();
  if (instance && !_hostAppContext) {
    _hostAppContext = instance.appContext;
    logger.trace("📦 Captured host appContext for popup inheritance");
  }
  /**
   * Mở popup theo key đã đăng ký
   * @param {string} popupKey - Key của popup trong POPUP_COMPONENTS
   * @param {object} props - Props truyền vào popup component
   * @param {object} options - Các tùy chọn popup
   * @returns {Promise} Promise resolve khi popup đóng với kết quả
   */
  const open = async (popupKey, props = {}, options = {}) => {
    if (!POPUP_COMPONENTS[popupKey]) {
      const error = new Error(`Popup "${popupKey}" không tồn tại trong registry!`);
      logger.error(error.message);
      throw error;
    }

    return new Promise(async (resolve, reject) => {
      try {
        const id = `popup-${++popupCounter.value}`;

        // Lazy load component nếu chưa cache
        let ComponentClass;
        if (componentCache.has(popupKey)) {
          ComponentClass = componentCache.get(popupKey);
        } else {
          logger.trace(`🔄 Lazy loading popup: ${popupKey}`);
          const componentModule = await POPUP_COMPONENTS[popupKey]();
          ComponentClass = componentModule.default || componentModule;
          componentCache.set(popupKey, ComponentClass);
          logger.trace(`✅ Popup ${popupKey} loaded and cached`);
        }

        // ✅ VIRTUAL MOUNT - Không tạo DOM container thật
        // Component sẽ tự Teleport vào body, ta chỉ cần virtual anchor
        const virtualMount = document.createElement("div");
        virtualMount.id = `popup-virtual-${id}`; // Chỉ để debug/reference
        // KHÔNG appendChild vào DOM!

        // Tạo popup instance
        const popup = {
          id,
          popupKey,
          virtualMount, // Virtual element không xuất hiện trong DOM
          componentRef: null,
          app: null,
          props: { ...props },
          options: {
            destroyOnClose: true,
            ...options,
          },
          resolve,
          reject,
          createdAt: Date.now(),
          _disposed: false, // Prevent double disposal
        };

        // Create Vue app instance for popup
        const app = createApp(ComponentClass, {
          ...popup.props,
          onClose: (result) => {
            popup.close(result);
          },
        });

        // Kế thừa components/directives từ host app thay vì app.use(MisaUI)
        // → tránh đăng ký lại toàn bộ component library mỗi lần mở popup
        if (_hostAppContext) {
          Object.assign(app._context.components, _hostAppContext.components);
          Object.assign(app._context.directives, _hostAppContext.directives);
          logger.trace(`🔗 Inherited host app context for popup ${id}`);
        } else {
          logger.warn(
            `⚠️ No host appContext captured — useDialog() phải được gọi trong setup() của một component`,
          );
        }

        app.use(i18n); // bổ sung i18n

        // Mount component vào virtual element
        // Component sẽ tự render qua <Teleport to="body">
        const componentInstance = app.mount(virtualMount);

        popup.app = app;
        popup.componentRef = componentInstance;

        // Enhanced close method với zero DOM pollution
        popup.close = (result) => {
          // Prevent double close calls
          if (popup._disposed) {
            logger.trace(`Popup ${id} already disposed, ignoring close call`);
            return;
          }
          popup._disposed = true;

          const index = activePopups.value.findIndex((p) => p.id === id);
          if (index !== -1) {
            logger.trace(`🔄 Disposing popup instance: ${id}`);

            // Hide popup first (trigger MSPopup animation)
            if (popup.componentRef?.hide) {
              popup.componentRef.hide();
            }

            // Enhanced cleanup after animation
            setTimeout(() => {
              try {
                // 1. Unmount Vue app (triggers proper component cleanup)
                // This will also clean up Teleport content automatically
                if (popup.app) {
                  popup.app.unmount();
                  popup.app = null;
                  logger.trace(`📱 Vue app ${id} unmounted`);
                }

                // 2. Clear virtual mount (không cần DOM removal vì không trong DOM)
                if (popup.virtualMount) {
                  popup.virtualMount = null;
                  logger.trace(`🗑️ Virtual mount ${id} cleared`);
                }

                // 3. Clear component reference
                popup.componentRef = null;

                // 4. Clear all object references
                popup.props = null;
                popup.options = null;
                popup.resolve = null;
                popup.reject = null;

                // 5. Remove from active list
                activePopups.value.splice(index, 1);

                logger.trace(`✅ Popup ${id} disposed completely (zero DOM residue)`);

                // Debug: Verify no DOM containers left
                if (import.meta.env.DEV) {
                  const containers = document.querySelectorAll('[id^="popup-container-"]');
                  const virtuals = document.querySelectorAll('[id^="popup-virtual-"]');
                  logger.trace(
                    `🔍 DOM check: ${containers.length} containers, ${virtuals.length} virtual elements (should be 0)`,
                  );
                }
              } catch (error) {
                logger.error(`❌ Error during popup disposal:`, error);
              }
            }, 300); // Delay for MSPopup close animation

            resolve(result);
          }
        };

        // Show popup
        if (componentInstance.show) {
          componentInstance.show(props, options);
        } else {
          // Component không có show method, assume auto-show
          logger.trace(`📺 Popup ${id} auto-showing`);
        }

        // Thêm vào danh sách active popups
        activePopups.value.push(popup);
        logger.trace(`🚀 Popup ${id} created and added to active list`);
      } catch (error) {
        logger.error(`❌ Lỗi khi tải popup ${popupKey}:`, error);
        reject(error);
      }
    });
  };

  /**
   * Đóng popup theo ID hoặc popupKey
   * @param {string} identifier - ID hoặc popupKey của popup
   * @param {any} result - Kết quả trả về
   */
  const close = (identifier, result) => {
    const popup = activePopups.value.find((p) => p.id === identifier || p.popupKey === identifier);

    if (popup) {
      popup.close(result);
    } else {
      logger.warn(`Không tìm thấy popup với identifier: ${identifier}`);
    }
  };

  /**
   * Đóng tất cả popup đang mở
   * @param {any} result - Kết quả trả về cho tất cả popup
   */
  const closeAll = (result) => {
    activePopups.value.forEach((popup) => popup.close(result));
  };

  /**
   * Đóng popup theo popupKey (tất cả instances)
   * @param {string} popupKey - Key của popup
   * @param {any} result - Kết quả trả về
   */
  const closeByKey = (popupKey, result) => {
    const popupsToClose = activePopups.value.filter((p) => p.popupKey === popupKey);
    popupsToClose.forEach((popup) => popup.close(result));
  };

  /**
   * Lấy thông tin các popup đang active
   */
  const getActivePopups = () => {
    return activePopups.value.map((popup) => ({
      id: popup.id,
      popupKey: popup.popupKey,
      createdAt: popup.createdAt,
      options: popup.options,
    }));
  };

  /**
   * Kiểm tra popup có đang mở hay không
   * @param {string} popupKey - Key của popup
   */
  const isOpen = (popupKey) => {
    return activePopups.value.some((p) => p.popupKey === popupKey);
  };

  /**
   * Đếm số lượng instances của popup
   * @param {string} popupKey - Key của popup
   */
  const countInstances = (popupKey) => {
    return activePopups.value.filter((p) => p.popupKey === popupKey).length;
  };

  /**
   * Mở popup xác nhận nhanh
   * @param {string} message - Nội dung xác nhận
   * @param {string} title - Tiêu đề popup
   * @param {object} options - Tùy chọn
   */
  const confirm = (message, title = "Xác nhận", options = {}) => {
    return open(
      "ConfirmDialog",
      { message, title },
      {
        ...options,
      },
    );
  };

  /**
   * Clear component cache (useful for development)
   */
  const clearCache = () => {
    componentCache.clear();
    logger.debug("Popup component cache cleared");
  };

  /**
   * Force dispose tất cả popup instances và clear cache
   * Sử dụng khi cần đảm bảo hoàn toàn memory clean
   */
  const forceCleanup = () => {
    logger.debug("Force cleanup all popup instances...");

    // Close all active popups
    activePopups.value.forEach((popup) => {
      try {
        if (popup.app) {
          popup.app.unmount();
        }
        if (popup.container && popup.container.parentNode) {
          popup.container.parentNode.removeChild(popup.container);
        }
      } catch (error) {
        logger.error("Error during force cleanup:", error);
      }
    });

    // Clear all references
    activePopups.value.length = 0;
    componentCache.clear();

    // Force garbage collection if available (dev environment)
    if (typeof window !== "undefined" && window.gc) {
      window.gc();
    }

    logger.debug("Force cleanup completed");
  };

  /**
   * Lấy memory usage statistics (development only)
   */
  const getMemoryStats = () => {
    const stats = {
      activePopupsCount: activePopups.value.length,
      cachedComponentsCount: componentCache.size,
      popupCounter: popupCounter.value,
    };

    if (typeof window !== "undefined" && window.performance && window.performance.memory) {
      stats.memoryUsage = {
        used: Math.round(window.performance.memory.usedJSHeapSize / 1024 / 1024) + " MB",
        total: Math.round(window.performance.memory.totalJSHeapSize / 1024 / 1024) + " MB",
        limit: Math.round(window.performance.memory.jsHeapSizeLimit / 1024 / 1024) + " MB",
      };
    }

    return stats;
  };

  return {
    // Core methods
    open,
    close,
    closeAll,
    closeByKey,

    // Helper methods
    confirm,
    isOpen,
    countInstances,
    getActivePopups,
    clearCache,
    forceCleanup,
    getMemoryStats,

    // State
    activePopups: readonly(activePopups),

    // Computed
    hasActivePopups: computed(() => activePopups.value.length > 0),
    topPopup: computed(() => activePopups.value[activePopups.value.length - 1]),
  };
}

/**
 * Đăng ký popup component mới
 * @param {string} key - Key để định danh popup
 * @param {Function|Object} component - Component hoặc dynamic import function
 */
export const registerPopup = (key, component) => {
  POPUP_COMPONENTS[key] = component;
  logger.info(`Popup "${key}" đã được đăng ký`);
};

/**
 * Hủy đăng ký popup component
 * @param {string} key - Key của popup
 */
export const unregisterPopup = (key) => {
  delete POPUP_COMPONENTS[key];
  componentCache.delete(key);
  logger.info(`Popup "${key}" đã được hủy đăng ký`);
};

/**
 * Lấy danh sách popup đã đăng ký
 */
export const getRegisteredPopups = () => {
  return Object.keys(POPUP_COMPONENTS);
};

/**
 * Preload popup component để cải thiện performance
 * @param {string} key - Key của popup
 */
export const preloadPopup = async (key) => {
  if (!POPUP_COMPONENTS[key]) {
    logger.warn(`Popup "${key}" không tồn tại`);
    return;
  }

  if (!componentCache.has(key)) {
    try {
      logger.debug(`Preloading popup: ${key}`);
      const componentModule = await POPUP_COMPONENTS[key]();
      const ComponentClass = componentModule.default || componentModule;
      componentCache.set(key, ComponentClass);
      logger.debug(`Popup "${key}" đã được preload`);
    } catch (error) {
      logger.error(`Lỗi khi preload popup ${key}:`, error);
    }
  }
};
